// ThreeDAvatar.jsx — Sprint 4A Fix 6: Single wave + immediate voice on tap
// CHANGES FROM SPRINT 4:
//   - Avatar speaks EXACTLY ONCE on wave — _greetingDone flag prevents double-fire
//   - No mouse-move required to activate voice
//   - Removed the "wait for interaction" fallback path that caused double waves
//   - speakGreeting() is called once: right after wave gesture, unconditionally
//   - App.js splash tap still handles AudioContext.resume() + synthesize directly
//     ThreeDAvatar's own greeting is now the BACKUP path (if splash already handled it, skip)

import React, { useEffect, useRef, useState } from "react";
import { AVATAR_OBJECTS, setCurrentAvatarObject } from "../control/botciergeController";

const MOOD_MAP = {
  neutral: "neutral", happy: "happy", sad: "sad",
  angry: "angry", love: "love", suggestive: "love",
};

function unlockAudioContext(audioCtx) {
  if (!audioCtx || audioCtx.state !== "suspended") return;
  try {
    const buf    = audioCtx.createBuffer(1, 1, 22050);
    const source = audioCtx.createBufferSource();
    source.buffer = buf;
    source.connect(audioCtx.destination);
    source.start(0);
    audioCtx.resume().then(() => console.log("[Audio] ✅ Auto-unlocked"));
  } catch(e) { console.warn("[Audio] unlock failed:", e); }
}

function hardStopAudio(head, headtts) {
  try {
    const gm  = head?.gainMaster;
    const ctx = head?.audioCtx;
    if (gm && ctx && ctx.state === "running") {
      gm.gain.cancelScheduledValues(ctx.currentTime);
      gm.gain.setValueAtTime(0, ctx.currentTime);
    }
  } catch(_) {}
  try { head?.stop?.(); }           catch(_) {}
  try { headtts?.terminate?.(); }   catch(_) {}
  try { headtts?.close?.(); }       catch(_) {}
  try { headtts?.port?.close?.(); } catch(_) {}
  try { head?.audioCtx?.suspend?.(); } catch(_) {}
}

export default function ThreeDAvatar({
  emotion,
  onReady,
  avatarName = "Camille",
  background = null,
  isTyping   = false,
}) {
  const avatarDivRef   = useRef(null);
  const headRef        = useRef(null);
  const headttsRef     = useRef(null);
  const listenersRef   = useRef({ move: null, leave: null, touch: null });
  const unlockRef      = useRef(null);
  const typingTimerRef = useRef(null);

  const [status,   setStatus]   = useState(`Loading ${avatarName}...`);
  const [progress, setProgress] = useState(0);
  const [loaded,   setLoaded]   = useState(false);

  // Typing reaction
  useEffect(() => {
    if (!headRef.current || !loaded) return;
    if (isTyping) {
      try { headRef.current.setValue("bodyRotateX", 0.04); } catch(_) {}
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        try { headRef.current.setValue("bodyRotateX", 0); } catch(_) {}
      }, 2000);
    }
  }, [isTyping, loaded]);

  // Main init
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setProgress(0);
    setStatus(`Loading ${avatarName}...`);

    if (avatarDivRef.current) avatarDivRef.current.innerHTML = "";

    // Remove old listeners
    const { move, leave, touch } = listenersRef.current;
    if (move)  { document.removeEventListener("mousemove",  move); document.removeEventListener("mouseleave", leave); }
    if (touch) { document.removeEventListener("touchmove",  touch); }
    if (unlockRef.current) {
      ["mousemove","touchstart","keydown","scroll"].forEach(ev =>
        document.removeEventListener(ev, unlockRef.current)
      );
    }
    listenersRef.current = { move: null, leave: null, touch: null };

    // Set global avatar object BEFORE anything else
    setCurrentAvatarObject(avatarName);

    // Kill switch
    window._avatarInstanceId = (window._avatarInstanceId || 0) + 1;
    const myInstanceId = window._avatarInstanceId;

    // Hard stop previous avatar audio
    if (headttsRef.current || headRef.current) {
      hardStopAudio(headRef.current, headttsRef.current);
      headRef.current          = null;
      headttsRef.current       = null;
      window.currentAvatarHead = null;
      window.currentAvatarTTS  = null;
    }

    const avatarObj = AVATAR_OBJECTS[avatarName] || AVATAR_OBJECTS.Camille;

    async function init() {
      if (!avatarDivRef.current) return;
      try {
        const origin = window.location.origin;
        setStatus("Loading engine...");
        setProgress(5);

        const { TalkingHead } = await import(/* webpackIgnore: true */ `${origin}/modules/talkinghead.mjs?v=20261029`);
        const { HeadTTS }     = await import(/* webpackIgnore: true */ `${origin}/modules/headtts.mjs?v=20260717c`);

        if (cancelled || window._avatarInstanceId !== myInstanceId) {
          console.log(`[${avatarName}] ⛔ Aborted after import`);
          return;
        }
        setProgress(15);

        const head = new TalkingHead(avatarDivRef.current, {
          ttsEndpoint: "N/A", lipsyncModules: [],
          cameraView: "upper", mixerGainSpeech: 3,
          cameraRotateEnable: false,
          lightAmbientIntensity: 2, lightDirectIntensity: 2, lightSpotIntensity: 0,
          avatarEyeContact: true,
          renderPixelRatio: window.devicePixelRatio || 2, antialias: true,
        });
        avatarDivRef.current.style.background = "transparent";
        headRef.current = head;
        setProgress(25);

        const wsProto   = window.location.protocol === "https:" ? "wss:" : "ws:";
        const httpProto = window.location.protocol;
        const hostname  = window.location.hostname;
        const isLocal   = hostname === "localhost" || hostname === "127.0.0.1";
        const ttsPort   = isLocal ? ":8882" : "";
        const ttsServer = `${httpProto}//${hostname}${ttsPort}/v1`;
        const ttsWs     = `${wsProto}//${hostname}${ttsPort}`;

        const headtts = new HeadTTS({
          endpoints: [ttsWs, ttsServer, "webgpu", "wasm"],
          model: "onnx-community/Kokoro-82M-v1.0-ONNX-timestamped",
          transformersModule: `${origin}/modules/transformers.min.js`,
          localModelPath: `${origin}/models/kokoro/`,
          languages: ["en-us"],
          voices: ["af_bella"],
          voiceURL: `${origin}/voices/`,
          audioCtx: head.audioCtx,
          trace: 0,
        });
        headttsRef.current = headtts;

        headtts.onmessage = (message) => {
          if (window._avatarInstanceId !== myInstanceId) {
            console.log(`[${avatarName}] ⛔ TTS dropped — stale instance`);
            return;
          }
          if (message.type === "audio") {
            // TASK 4: fire the upsell wink right after the spoken word "Purchase".
            // Kokoro-82M-…-timestamped returns word marks: words[] / wtimes[] / wdurations[] (ms).
            // Read-only — the audio payload and lip-sync below are untouched.
            try {
              const mark = window._winkAfterWord;
              const words = message.data?.words;
              if (mark && Array.isArray(words)) {
                const i = words.findIndex(w => String(w).toLowerCase().includes(mark));
                if (i >= 0) {
                  const t = (message.data.wtimes?.[i] ?? 0) + (message.data.wdurations?.[i] ?? 0);
                  window._winkAfterWord = null;
                  setTimeout(() => {
                    try { window.controlBotcierge?.({ avatarType: "3D", emotion: "suggestive" }); } catch(_) {}
                  }, t);
                }
              }
            } catch(_) {}
            try {
              const rawMood = headRef.current?._currentMood || "neutral";
              const mood    = MOOD_MAP[rawMood] || "neutral";
              if (typeof head.speakMarker === "function") head.speakMarker(() => {});
              head.speakAudio(message.data, { mood }, () => {
                console.log(`[${avatarName}] Done speaking`);
              });
            } catch (err) { console.error("[TTS] speakAudio error:", err); }
          } else if (message.type === "error") {
            console.error("[TTS] error:", message.data?.error);
          }
        };

        setStatus(`Loading ${avatarName}...`);
        setProgress(30);

        const avatarPromise = head.showAvatar(
          { url: avatarObj.glb, body: avatarObj.body, avatarMood: "neutral", scale: 0.75 },
          (ev) => {
            if (ev?.lengthComputable) {
              const pct = Math.round((ev.loaded / ev.total) * 100);
              setProgress(30 + Math.round(pct * 0.55));
              setStatus(`Loading ${avatarName}... ${pct}%`);
            }
          }
        );

        const ttsPromise = headtts.connect(null, () => {})
          .then(() => { if (!cancelled) console.log("[TTS] Connected ✅"); })
          .catch(e  => console.warn("[TTS] connect failed:", e));

        // GREETING_PREFETCH (REACT_APP_GREETING_PREFETCH_ENABLED, default on): ask
        // for the greeting audio while the GLB loads, so the voice starts WITH the
        // wave instead of after a reconnect + synthesis (seconds). The website
        // server also keeps every avatar's guest greeting ready (proxy/ttsCache.mjs).
        const greetingFetch = { text: null, audio: null };
        const greetingReady = process.env.REACT_APP_GREETING_PREFETCH_ENABLED === "false" ? Promise.resolve() :
          ttsPromise.then(async () => {
            if (cancelled || window._avatarInstanceId !== myInstanceId) return;
            const text = window._pendingGreeting || `Hi! I'm ${avatarName}. What is your name?`;
            headtts.setup({ voice: avatarObj.voice, language: avatarObj.language, speed: avatarObj.speed, audioEncoding: avatarObj.encoding });
            const out = [];
            await headtts.synthesize({ input: text }, (m) => { if (m?.type === "audio") out.push(m.data); });
            if (out.length) { greetingFetch.text = text; greetingFetch.audio = out; }
          }).catch(() => {});
        window._greetingPrefetch = { instance: myInstanceId, fetch: greetingFetch, ready: greetingReady };

        await avatarPromise;
        void ttsPromise;

        if (cancelled || window._avatarInstanceId !== myInstanceId) {
          console.log(`[${avatarName}] ⛔ Aborted after GLB load`);
          hardStopAudio(head, headtts);
          return;
        }

        setProgress(88);

        const cvs = avatarDivRef.current?.querySelector("canvas");
        if (cvs) cvs.style.background = "transparent";
        if (head.renderer) head.renderer.setClearColor(0x000000, 0);

        const view = {
          cameraY:       avatarObj.cameraY,
          cameraX:       avatarObj.cameraX,
          cameraFOV:     avatarObj.cameraFOV,
          cameraRotateX: avatarObj.cameraRotateX,
          cameraRotateY: avatarObj.cameraRotateY,
          cameraRotateZ: avatarObj.cameraRotateZ,
        };
        head.setView(head.viewName, view);
        head.cameraClock = 999;
        if (avatarObj.cameraFOV && head.camera) {
          head.camera.fov = avatarObj.cameraFOV;
          head.camera.updateProjectionMatrix();
        }

        // Set encapsulated voice
        headtts.setup({
          voice:         avatarObj.voice,
          language:      avatarObj.language,
          speed:         avatarObj.speed,
          audioEncoding: avatarObj.encoding,
        });
        console.log(`[${avatarName}] ✅ Voice set: ${avatarObj.voice}`);

        setProgress(95);

        // Audio auto-unlock (for cases where no tap occurred)
        const tryUnlock = () => {
          if (head.audioCtx?.state === "suspended") unlockAudioContext(head.audioCtx);
          if (headttsRef.current) headttsRef.current.connect(null, () => {}).catch(() => {});
          ["mousemove","touchstart","keydown","scroll"].forEach(ev =>
            document.removeEventListener(ev, tryUnlock)
          );
          unlockRef.current = null;
        };
        unlockRef.current = tryUnlock;
        ["mousemove","touchstart","keydown","scroll"].forEach(ev =>
          document.addEventListener(ev, tryUnlock, { once: true, passive: true })
        );

        // Set globals
        window.currentAvatarHead = head;
        window.currentAvatarTTS  = headtts;
        window._3dActive         = true;
        window.camilleResumeAudio = () => {
          if (head.audioCtx?.state === "suspended") unlockAudioContext(head.audioCtx);
        };

        // ── FIX 6: Single greeting — wave fires once, speech fires once ──────
        // The WS often closes with code 1006 (Heroku 55s timeout) while GLB loads.
        // doSpeak now reconnects HeadTTS before synthesizing if WS is dead.
        setTimeout(() => {
          if (cancelled || window._avatarInstanceId !== myInstanceId) return;
          try { headRef.current.setMood("happy"); } catch(_) {}

          const wave = () => { try { headRef.current?.playGesture("👋", 1.5); } catch(_) {} };

          // Check if App.js splash tap already handled the greeting
          if (window._greetingDone) {
            wave(); // the wave plays ONCE either way
            console.log(`[${avatarName}] Greeting already spoken by splash tap — skipping duplicate`);
            return;
          }

          const greetingText = window._pendingGreeting || `Hi! I'm ${avatarName}. What is your name?`;
          window._pendingGreeting = null;
          window._greetingDone   = true;

          // The pre-fetched greeting: wave and voice start together.
          const speakPrefetched = () => {
            if (window._avatarInstanceId !== myInstanceId) return true;
            if (greetingFetch.text !== greetingText || !greetingFetch.audio) return false;
            const mood = MOOD_MAP[headRef.current?._currentMood] || "neutral";
            wave();
            try { greetingFetch.audio.forEach((d) => head.speakAudio(d, { mood }, () => {})); } catch (e) { console.warn("[TTS] prefetched greeting failed:", e?.message); return false; }
            console.log(`[${avatarName}] ✅ Greeting spoken with the wave (pre-fetched)`);
            return true;
          };
          // Wait up to 3 s for the pre-fetch, then the old path (wave, then reconnect + speak).
          const withPrefetch = (fallback) => Promise.race([greetingReady, new Promise((r) => setTimeout(r, 3000))])
            .then(() => { if (!speakPrefetched()) { wave(); fallback(); } });

          // ── speakWithReconnect: ALWAYS reconnects before speaking ───────────
          // ROOT CAUSE: tts.ws is undefined on this HeadTTS version (not exposed).
          // So we can't check readyState. Instead: ALWAYS call connect() before speak.
          // connect() is safe to call even if already open — resolves in <100ms.
          // This guarantees a live WS after the GLB loads (which takes 60s+ on Heroku,
          // causing the original WS to die with code 1006 before speech fires).
          const speakWithReconnect = () => {
            if (window._avatarInstanceId !== myInstanceId) return;
            const tts = headttsRef.current;
            if (!tts) {
              console.warn(`[${avatarName}] No TTS instance — cannot speak`);
              return;
            }

            const doSynthesize = () => {
              try {
                // Always re-apply voice setup after reconnect (setup is per-connection)
                tts.setup({
                  voice:         avatarObj.voice,
                  language:      avatarObj.language,
                  speed:         avatarObj.speed,
                  audioEncoding: avatarObj.encoding,
                });
                tts.synthesize({ input: greetingText });
                console.log(`[${avatarName}] ✅ Greeting spoken after wave`);
              } catch(e) {
                console.warn(`[${avatarName}] synthesize failed:`, e.message);
              }
            };

            // Always reconnect — don't check .ws (not exposed by HeadTTS)
            // GLB takes 60s+ on Heroku → original WS dies → reconnect guarantees fresh WS
            console.log(`[${avatarName}] Reconnecting WS before greeting...`);
            tts.connect(null, () => {})
              .then(() => {
                console.log(`[${avatarName}] ✅ WS ready — speaking now`);
                doSynthesize();
              })
              .catch(e => {
                // connect() failed — try synthesize anyway (WASM/webgpu fallback in HeadTTS)
                console.warn(`[${avatarName}] connect failed, trying anyway:`, e?.message || e);
                doSynthesize();
              });
          };

          if (head.audioCtx?.state === "running") {
            withPrefetch(speakWithReconnect);
          } else {
            // AudioContext suspended. After an avatar switch the page has already
            // had a click, so resume() usually works at once; otherwise the first
            // click/tap/key (or anything else that unlocks audio) starts it. The
            // greeting goes the moment audio runs, wave and voice together.
            const events = ["click", "touchstart", "keydown"];
            let greeted = false;
            const ctx = head.audioCtx;
            const greet = () => {
              if (greeted || window._avatarInstanceId !== myInstanceId) return;
              greeted = true;
              events.forEach(ev => document.removeEventListener(ev, onFirstInteract));
              try { ctx?.removeEventListener?.("statechange", onState); } catch(_) {}
              withPrefetch(speakWithReconnect);
            };
            const onState = () => { if (ctx?.state === "running") greet(); };
            const onFirstInteract = () => {
              if (ctx?.state === "suspended") ctx.resume().then(greet).catch(greet);
              else greet();
            };
            try { ctx?.addEventListener?.("statechange", onState); } catch(_) {}
            events.forEach(ev => document.addEventListener(ev, onFirstInteract, { once: true, passive: true }));
            ctx?.resume?.().then(onState).catch(() => {});
          }
        }, 0);

        // Cursor / touch tracking
        let lastLook = 0;
        const handleMouseMove = (e) => {
          const now = Date.now();
          if (now - lastLook < 80) return;
          lastLook = now;
          if (!headRef.current) return;
          try {
            const q = headRef.current.animQueue;
            if (q) { const i = q.findIndex(a => a?.template?.name === "lookat"); if (i !== -1) q.splice(i, 1); }
            headRef.current.lookAt(e.clientX, e.clientY, 1);
            headRef.current.setValue("bodyRotateY", 0);
            headRef.current.setValue("bodyRotateX", 0);
          } catch(_) {}
        };
        const handleMouseLeave = () => {
          if (!headRef.current) return;
          try { headRef.current.lookAt(window.innerWidth / 2, window.innerHeight / 3, 500); } catch(_) {}
        };
        document.addEventListener("mousemove",  handleMouseMove);
        document.addEventListener("mouseleave", handleMouseLeave);

        let lastTouch = 0;
        const handleTouchMove = (e) => {
          const now = Date.now();
          if (now - lastTouch < 80) return;
          lastTouch = now;
          if (!headRef.current) return;
          try {
            const t = e.touches[0];
            const q = headRef.current.animQueue;
            if (q) { const i = q.findIndex(a => a?.template?.name === "lookat"); if (i !== -1) q.splice(i, 1); }
            headRef.current.lookAt(t.clientX, t.clientY, 1);
            headRef.current.setValue("bodyRotateY", 0);
            headRef.current.setValue("bodyRotateX", 0);
          } catch(_) {}
        };
        document.addEventListener("touchmove", handleTouchMove, { passive: true });
        listenersRef.current = { move: handleMouseMove, leave: handleMouseLeave, touch: handleTouchMove };

        setProgress(100);
        setLoaded(true);
        setStatus("");
        console.log(`[${avatarName}] ✅ All systems ready | Voice: ${avatarObj.voice}`);
        if (onReady) onReady({ head, headtts });

      } catch (err) {
        console.error(`[${avatarName} init error]`, err);
        setStatus("Error: " + err.message);
        setProgress(0);
        if (onReady) onReady(null);
      }
    }

    init();

    return () => {
      cancelled = true;
      clearTimeout(typingTimerRef.current);
      const { move, leave, touch } = listenersRef.current;
      if (move)  { document.removeEventListener("mousemove",  move); document.removeEventListener("mouseleave", leave); }
      if (touch) { document.removeEventListener("touchmove",  touch); }
      if (unlockRef.current) {
        ["mousemove","touchstart","keydown","scroll"].forEach(ev =>
          document.removeEventListener(ev, unlockRef.current)
        );
      }
      listenersRef.current = { move: null, leave: null, touch: null };
      hardStopAudio(headRef.current, headttsRef.current);
      window._3dActive = false;
      if (window.currentAvatarHead === headRef.current)  window.currentAvatarHead = null;
      if (window.currentAvatarTTS  === headttsRef.current) window.currentAvatarTTS  = null;
      headttsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onReady is stable enough; parent inline fn must not re-trigger GLB load
  }, [avatarName]);

  // Emotion changes
  useEffect(() => {
    if (!headRef.current || !loaded) return;
    const mood = emotion || "neutral";
    try {
      const validMood = MOOD_MAP[mood] || "neutral";
      if (mood === "suggestive") {
        const r = headRef.current.setMood("love");
        if (r?.then) r.then(() => headRef.current?.playGesture("😉", 1)).catch(() => headRef.current?.playGesture("😉", 1));
        else headRef.current.playGesture("😉", 1);
      } else {
        const r = headRef.current.setMood(validMood);
        if (r?.then) r.catch(console.warn);
      }
      headRef.current._currentMood = mood;
    } catch (e) { console.warn(`[${avatarName}] setMood error:`, e); }
  }, [emotion, loaded, avatarName]);

  return (
    <div style={{ ...s.wrapper, background: background ? `url(${background}) center/cover no-repeat` : "transparent" }}>
      <div ref={avatarDivRef} style={s.avatarDiv} />
      {!loaded && (
        <div style={s.overlay}>
          <img src="/boticon.png" alt="loading"
            style={{ width:40, height:40, objectFit:"contain", marginBottom:4, animation:"spin 3s linear infinite", filter:"drop-shadow(0 2px 8px rgba(107,78,170,0.4))" }} />
          <p style={s.statusText}>{status}</p>
          <div style={{ width:"160px", height:"3px", background:"rgba(255,255,255,0.2)", borderRadius:"4px", overflow:"hidden", marginTop:"4px" }}>
            <div style={{ height:"100%", width:`${progress}%`, background:"linear-gradient(90deg,#9B7FD4,#6B4EAA)", borderRadius:"4px", transition:"width 0.4s ease" }} />
          </div>
          <p style={{ fontSize:"11px", color:"rgba(255,255,255,0.7)", marginTop:"2px" }}>{progress}%</p>
        </div>
      )}
    </div>
  );
}

const s = {
  wrapper:    { position:"relative", width:"100%", height:"100%", overflow:"hidden" },
  avatarDiv:  { width:"100%", height:"100%" },
  overlay:    { position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.3)", backdropFilter:"blur(4px)", gap:"8px" },
  statusText: { color:"rgba(255,255,255,0.9)", fontSize:"13px", margin:0, textAlign:"center", padding:"0 20px" },
};
